import format from 'date-fns/format';
import i18n from '~shared/utils/i18n';
import { getFormattedDateTime, getLocalizedDateFormat } from '~shared/utils/date-time';
import { getLocaleTimeZone } from '~shared/utils/bootstrap-timezone';
import { getGaAttributeObjectSportPage } from '~shared/utils/google-analytics';

import { MATCHES_TAB_MENU_SLUGS } from './constants';
import { DATE_TIME_FORMAT_DEFAULT } from '~shared/utils/constants';

export const getEventsForTabUrl = (sportSlug, tabSlug) => `sportsbook-req/getUpcomingEvents/${sportSlug}/${tabSlug}`;

/**
 * Process the event digest data returned by end point and group them into categories to find out the active
 * carousel items for the sports matches page.
 * Today's count in the digest is excluding live and starting_soon count, so if we have any live or
 * starting_soon also will be considered as today in the digest
 * @param {object} digest - object returned by the events digest endpoint
 * @param {string} sportSlug - sports slug
 * @returns {array} - data for carousel items
 */
export const normaliseEventsDigest = (digest) => {
    let carouselItems = [];
    if (digest?.live > 0 || digest?.starting_soon > 0) {
        carouselItems.push({
            slug: MATCHES_TAB_MENU_SLUGS.LIVE,
            display: i18n.t(`sports.${MATCHES_TAB_MENU_SLUGS.LIVE}`),
        });
    }
    if (digest?.today > 0 || digest?.live > 0 || digest?.starting_soon > 0) {
        carouselItems.push({
            slug: MATCHES_TAB_MENU_SLUGS.TODAY,
            display: i18n.t(`sports.${MATCHES_TAB_MENU_SLUGS.TODAY}`),
        });
    }
    if (digest?.tomorrow > 0) {
        carouselItems.push({
            slug: MATCHES_TAB_MENU_SLUGS.TOMORROW,
            display: i18n.t(`sports.${MATCHES_TAB_MENU_SLUGS.TOMORROW}`),
        });
    }
    if (Object.keys(digest?.upcoming).length) {
        if (digest.display_day_tabs) {
            Object.keys(digest.upcoming)
            .sort()
            .forEach(date => {
                const dateObj = new Date(`${date}T00:00:00`); // force constructor to treat param as a local datetime
                carouselItems.push({
                    slug: String(date),
                    display: getFormattedDateTime(dateObj),
                });
            });
        } else {
            carouselItems.push({
                slug: MATCHES_TAB_MENU_SLUGS.UPCOMING,
                display: i18n.t(`sports.${MATCHES_TAB_MENU_SLUGS.UPCOMING}`),
            });
        }
    }

    return carouselItems;
};

/**
 * Group events by tournament slug
 * @param {Array} allEvents list of events from BE
 * @returns {Array} events grouped by date
 */
export const groupEventsByDate = (allEvents) => {
    return allEvents.reduce(function (groupedEvents, event) {
        const start_date = new Date(event.start_time);
        let key = start_date.toLocaleDateString('en-CA');
        if (!groupedEvents[key]) {
            groupedEvents[key] = {
                id: start_date.getTime(),
                formattedDate: format(
                    start_date,
                    getLocalizedDateFormat(DATE_TIME_FORMAT_DEFAULT),
                    { locale: getLocaleTimeZone() }
                ),
                events: [],
            };
        }
        groupedEvents[key].events.push(event);

        return groupedEvents;
    }, {});
};

/**
 * Group events by tournament slug
 * @param {Array} allEvents list of events from BE
 * @returns {Object} events grouped by tournament (keyed by tournament slug)
 */
export const groupEventsByTournamentSlug = (allEvents) => {

    const grouped = allEvents.reduce((groupedEvents, event) => {
        const key = event.tournament_slug;
        if (!groupedEvents[key]) {
            const {
                sport_slug,
                sport_slug_i18n,
                category_slug_i18n,
                category_slug,
                tournament_id: tournamentId,
                tournament_display_name: tournamentName,
                tournament_slug,
                tournament_slug_i18n,
            } = event;
            groupedEvents[key] = {
                sportSlug: sport_slug,
                sportSlugI18n: sport_slug_i18n,
                categorySlug: category_slug,
                categorySlugI18n: category_slug_i18n,
                tournamentId,
                tournamentName,
                tournamentSlug: tournament_slug,
                tournamentSlugI18n: tournament_slug_i18n,
                events: [],
            };
        }
        groupedEvents[key].events.push(event);

        return groupedEvents;
    }, {});

    return grouped;
};

/**
 * Normalise/Filter common data events in the given order
 * @param {Array} commonDataEvents fetched events from common data store
 * @param {Array} orderedEvents fetched events with market details from common data store
 * @returns {Array} normalised events
 */
export const normaliseCommonDataEvents = (commonDataEvents, orderedEvents) => {
    const normalisedCommonDataEvents = [];
    orderedEvents.forEach((eventSnippet) => {
        const { event_id, market_id, grouped_market_ids } = eventSnippet;
        const event = commonDataEvents[event_id];
        if (!event) {

            return;
        }

        normalisedCommonDataEvents.push({ ...event, market_id, grouped_market_ids });
    });

    return normalisedCommonDataEvents;
};

/**
 * Retrieve GA/BetLocate object for an active tab for use in DOM
 * @param {String} sportSlug sportslug
 * @param {String} activeTab tab
 * @returns {String} GA/BetLocate value
 */
export const getGaAttributeObjectForTab = (sportSlug, activeTab) => {
    const tabForBetLocate = ['live', 'today', 'tomorrow'].includes(activeTab)
        ? activeTab
        : 'upcoming';

    return getGaAttributeObjectSportPage(sportSlug, tabForBetLocate);
};
